/**
 * Pro-Polygon-Aggregator (Sprint G2.1 — 2026-05-02)
 *
 * Pure Function: nimmt ein Polygon-Feature + Katalog und liefert eine
 * strukturierte Aggregation pro Bauteil-Sektion (Boden / Decke / Wände /
 * Sanitär / Türen / Fenster) plus die flache LV-Positions-Liste für genau
 * dieses Polygon.
 *
 * Wird vom Frontend-Detail-Panel (🎯-Tab) zur Pro-Raum-Übersicht und vom
 * Excel-Generator (Sprint G2 — Raumbuch / Türenliste / Fensterliste) wieder-
 * verwendet. Backend `/quantities-v2` macht im Wesentlichen das Gleiche, aber
 * über alle Polygone hinweg (mit Aggregaten pro Hauptgruppe).
 *
 * Design-Entscheidung: keine Daten-Quelle (kein File-I/O) — der Aufrufer
 * übergibt Polygon + Katalog + Evaluator. Damit wird der Aggregator sowohl
 * im Browser als auch im Backend benutzbar (und unit-testbar ohne Mocks).
 */

import {
  evaluateMengenFormel,
  type FormelContext,
  type EvaluationResult,
} from "./mengen-formel-evaluator.js";

// ============================================================================
// Polygon-Schnittstelle (minimal, kompatibel zu PolygonFeature aus
// frontend/multiagent-types.ts und core/agents/polygon-builder/index.ts)
// ============================================================================

/**
 * Minimaler Polygon-Snapshot — Felder die der Aggregator braucht. Konkrete
 * Polygon-Feature-Typen (Frontend `PolygonFeature` / Core `PolygonBuilderFeature`)
 * sind Supersets und können ohne Cast übergeben werden.
 */
export interface PolygonForAggregator {
  properties: {
    id: string;
    label?: string;
    areaM2?: number;
    perimeterM?: number;
    isBuilding?: boolean;
    walls?: WallForAggregator[];
    openings?: OpeningForAggregator[];
    sanitaerObjekte?: SanitaerObjektForAggregator[];
    codeAssignments?: CodeAssignmentForAggregator[];
    valueSet?: {
      effective?: { L?: number; A?: number; rh?: { min: number; max?: number } };
    };
  };
}

export interface WallForAggregator {
  wallId?: string;
  lengthMm?: number;
  totalThicknessMm?: number;
  codeAssignments?: CodeAssignmentForAggregator[];
}

export interface OpeningForAggregator {
  type: "door" | "window";
  widthM: number;
  heightM?: number;
  bruestungM?: number;
  id?: string;
  label?: string;
}

export interface SanitaerObjektForAggregator {
  id: string;
  type: string;
  hersteller?: string;
  modell?: string;
}

export interface CodeAssignmentForAggregator {
  id: string;
  code: string;
  source: string;
  status?: string;
}

export interface CatalogEntryForAggregator {
  code: string;
  fullCode?: string;
  gewerk: string;
  shortLabel?: string;
  lvMapping?: {
    positions: Array<{
      hauptgruppe: string;
      untergruppe?: string;
      ozPattern: string;
      bezeichnung: string;
      langtextTemplate: string;
      mengenEinheit: string;
      mengenFormel: string;
    }>;
  };
}

// ============================================================================
// Output-Typen
// ============================================================================

/** Section-Tag für die UI-Gruppierung. */
export type BauteilSection =
  | "boden"
  | "decke"
  | "waende"
  | "sanitaer"
  | "tueren"
  | "fenster"
  | "sonstiges";

/** Mapping Gewerk-Prefix → Section. AW/IW/FAS sind Wand-Codes. */
export const GEWERK_PREFIX_TO_SECTION: Record<string, BauteilSection> = {
  AW: "waende",
  IW: "waende",
  FAS: "waende",
  WA: "waende",
  BO: "boden",
  // Sprint P-Iteration (2026-05-04) — GA-Codes (GaLa-Außenbeläge:
  // Pflaster/Holzdiele/WPC/Steinplatte/Rasen/Kies) zählen für die UI als
  // Boden-Section, damit sie im 🟫-Boden-Picker für outdoor-Polygone landen.
  GA: "boden",
  DE: "decke",
  SAN: "sanitaer",
  TU: "tueren",
  FE: "fenster",
};

export interface PositionForRoom {
  /** Verweis auf den Code (z.B. "AW02", "BO-PARKETT-KLEB"). */
  code: string;
  /** Gewerk-Prefix aus dem Katalog. */
  gewerk: string;
  /** Quelle der CodeAssignment. */
  source: string;
  /** "polygon" oder "wall" — welche Ebene hat den Code zugewiesen? */
  scope: "polygon" | "wall";
  /** Wand-ID falls scope=wall. */
  wallId?: string;
  hauptgruppe: string;
  untergruppe?: string;
  ozPattern: string;
  bezeichnung: string;
  langtextTemplate: string;
  menge: number;
  einheit: string;
  mengenAusdruck: string;
  mengenFormel: string;
}

export interface CodeForSection {
  code: string;
  gewerk: string;
  shortLabel?: string;
  source: string;
  scope: "polygon" | "wall";
  wallId?: string;
  /** Anzahl Wände, an denen dieser Code zugewiesen ist (nur bei scope=wall). */
  wallCount?: number;
}

export interface RoomQuantityAggregate {
  polygonId: string;
  polygonLabel: string;
  effective: { L: number; A: number; rh: number };
  isBuilding: boolean;
  /** Strukturierte Sektion-Übersicht für UI. */
  sections: {
    boden: { codes: CodeForSection[] };
    decke: { codes: CodeForSection[] };
    waende: { codes: CodeForSection[] };
    sanitaer: {
      codes: CodeForSection[];
      objekte: SanitaerObjektForAggregator[];
      sanCount: Record<string, number>;
    };
    tueren: { codes: CodeForSection[]; openings: OpeningForAggregator[] };
    fenster: { codes: CodeForSection[]; openings: OpeningForAggregator[] };
    sonstiges: { codes: CodeForSection[] };
  };
  /** Flache LV-Position-Liste — gleiche Struktur wie /quantities-v2. */
  lvPositions: PositionForRoom[];
  /** Codes ohne lvMapping (im Katalog vorhanden, aber LV-Mapping leer). */
  unmappedCodes: Array<{ code: string; scope: "polygon" | "wall" }>;
  /** Codes nicht im Katalog. */
  unknownCodes: Array<{ code: string; scope: "polygon" | "wall" }>;
}

// ============================================================================
// Public API
// ============================================================================

export interface AggregateOptions {
  defaultRaumhoeheM: number;
  verschnittPct: number;
}

/**
 * Aggregiert ein Polygon zu einer strukturierten Pro-Raum-Übersicht. Reine
 * Funktion — kein File-I/O, kein Network. Caller muss Polygon + Katalog
 * + Aggregat-Optionen liefern.
 */
export function aggregateRoomQuantities(
  polygon: PolygonForAggregator,
  catalog: CatalogEntryForAggregator[],
  opts: AggregateOptions
): RoomQuantityAggregate {
  const props = polygon.properties;
  const polyId = props.id;
  const polyLabel = props.label ?? polyId;
  const isBuilding = props.isBuilding === true;

  const effA = props.valueSet?.effective?.A ?? props.areaM2 ?? 0;
  const effL = props.valueSet?.effective?.L ?? props.perimeterM ?? 0;
  const effRh =
    props.valueSet?.effective?.rh?.min ?? opts.defaultRaumhoeheM;
  const wandflaecheM2 = effL * effRh;

  // Σ Öffnungs-Fläche (Polygon-Scope).
  let oeffnungenM2Polygon = 0;
  for (const o of props.openings ?? []) {
    if (
      typeof o.widthM === "number" &&
      typeof o.heightM === "number" &&
      o.widthM > 0 &&
      o.heightM > 0
    ) {
      oeffnungenM2Polygon += o.widthM * o.heightM;
    }
  }

  // sanCount aus sanitaerObjekte ableiten.
  const sanCount: Record<string, number> = {
    WC: 0,
    WT: 0,
    DUSCHE: 0,
    BADEWANNE: 0,
    URINAL: 0,
    KUECHENZEILE: 0,
  };
  for (const obj of props.sanitaerObjekte ?? []) {
    const key = String(obj.type).toUpperCase();
    sanCount[key] = (sanCount[key] ?? 0) + 1;
  }

  // tuerCount + fensterCount aus openings[type] ableiten.
  let tuerCount = 0;
  let fensterCount = 0;
  for (const o of props.openings ?? []) {
    if (o.type === "door") tuerCount++;
    else if (o.type === "window") fensterCount++;
  }

  const polygonCtx: FormelContext["polygon"] = {
    wandflaecheM2,
    wandflaecheInnenM2: wandflaecheM2,
    wandflaecheAussenM2: wandflaecheM2 / 2,
    bodenflaecheM2: effA,
    deckenflaecheM2: effA,
    umfangM: effL,
    volumenM3: effA * effRh,
    sanCount,
    tuerCount,
    fensterCount,
  };

  const codeIndex = new Map(catalog.map((e) => [e.code, e]));
  const fullCodeIndex = new Map(
    catalog.map((e) => [e.fullCode ?? e.code, e])
  );
  const lookup = (code: string): CatalogEntryForAggregator | undefined =>
    codeIndex.get(code) ?? fullCodeIndex.get(code);

  const lvPositions: PositionForRoom[] = [];
  const unmappedCodes: RoomQuantityAggregate["unmappedCodes"] = [];
  const unknownCodes: RoomQuantityAggregate["unknownCodes"] = [];

  // Sammle pro Section die zugewiesenen Codes (mit Aggregation der Wand-Codes
  // per Code-ID — pro Wand zugewiesen aber im UI als „Code an N Wänden").
  const polygonSectionCodes: Map<BauteilSection, CodeForSection[]> = new Map();
  const wallSectionCodesByCode: Map<string, CodeForSection> = new Map();
  const wallSectionRaw: CodeForSection[] = [];

  // 1. Polygon-Scope CodeAssignments
  for (const ca of props.codeAssignments ?? []) {
    const entry = lookup(ca.code);
    if (!entry) {
      unknownCodes.push({ code: ca.code, scope: "polygon" });
      continue;
    }
    const section = sectionForGewerk(entry.gewerk);
    const codeEntry: CodeForSection = {
      code: ca.code,
      gewerk: entry.gewerk,
      shortLabel: entry.shortLabel,
      source: ca.source,
      scope: "polygon",
    };
    const arr = polygonSectionCodes.get(section) ?? [];
    arr.push(codeEntry);
    polygonSectionCodes.set(section, arr);

    // LV-Positionen
    if (!entry.lvMapping || entry.lvMapping.positions.length === 0) {
      unmappedCodes.push({ code: ca.code, scope: "polygon" });
      continue;
    }
    appendPositions(
      lvPositions,
      entry,
      ca,
      "polygon",
      undefined,
      polygonCtx,
      oeffnungenM2Polygon,
      opts.verschnittPct
    );
  }

  // 2. Wand-Scope CodeAssignments — pro Code aggregieren (gleicher Code an
  // mehreren Wänden = ein Eintrag mit wallCount).
  for (const wall of props.walls ?? []) {
    const wandLaengeM = (wall.lengthMm ?? 0) / 1000;
    const wandFlaecheM2 = wandLaengeM * effRh;
    const wandOeffnungenM2 =
      effL > 0 ? (oeffnungenM2Polygon * wandLaengeM) / effL : 0;

    for (const ca of wall.codeAssignments ?? []) {
      const entry = lookup(ca.code);
      if (!entry) {
        unknownCodes.push({ code: ca.code, scope: "wall" });
        continue;
      }
      const section = sectionForGewerk(entry.gewerk);
      const aggKey = `${ca.code}|${ca.source}`;
      const existing = wallSectionCodesByCode.get(aggKey);
      if (existing) {
        existing.wallCount = (existing.wallCount ?? 1) + 1;
      } else {
        const codeEntry: CodeForSection = {
          code: ca.code,
          gewerk: entry.gewerk,
          shortLabel: entry.shortLabel,
          source: ca.source,
          scope: "wall",
          wallId: wall.wallId,
          wallCount: 1,
        };
        wallSectionCodesByCode.set(aggKey, codeEntry);
        wallSectionRaw.push(codeEntry);
        const arr = polygonSectionCodes.get(section) ?? [];
        arr.push(codeEntry);
        polygonSectionCodes.set(section, arr);
      }

      // LV-Position pro Wand evaluieren (jeder Code generiert pro Wand
      // separate Positionen — Sum kommt im /quantities-v2-Aggregator).
      if (!entry.lvMapping || entry.lvMapping.positions.length === 0) {
        unmappedCodes.push({ code: ca.code, scope: "wall" });
        continue;
      }
      appendPositions(
        lvPositions,
        entry,
        ca,
        "wall",
        wall.wallId,
        polygonCtx,
        wandOeffnungenM2,
        opts.verschnittPct,
        { flaecheM2: wandFlaecheM2, laengeM: wandLaengeM, dickeMm: wall.totalThicknessMm ?? 0 }
      );
    }
  }

  // Türen + Fenster: codes (TU/FE-CodeAssignments) + actual openings.
  const tuerOpenings = (props.openings ?? []).filter((o) => o.type === "door");
  const fensterOpenings = (props.openings ?? []).filter((o) => o.type === "window");

  return {
    polygonId: polyId,
    polygonLabel: polyLabel,
    effective: { L: effL, A: effA, rh: effRh },
    isBuilding,
    sections: {
      boden: { codes: polygonSectionCodes.get("boden") ?? [] },
      decke: { codes: polygonSectionCodes.get("decke") ?? [] },
      waende: { codes: polygonSectionCodes.get("waende") ?? [] },
      sanitaer: {
        codes: polygonSectionCodes.get("sanitaer") ?? [],
        objekte: props.sanitaerObjekte ?? [],
        sanCount,
      },
      tueren: {
        codes: polygonSectionCodes.get("tueren") ?? [],
        openings: tuerOpenings,
      },
      fenster: {
        codes: polygonSectionCodes.get("fenster") ?? [],
        openings: fensterOpenings,
      },
      sonstiges: { codes: polygonSectionCodes.get("sonstiges") ?? [] },
    },
    lvPositions,
    unmappedCodes,
    unknownCodes,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function sectionForGewerk(gewerk: string): BauteilSection {
  return GEWERK_PREFIX_TO_SECTION[gewerk] ?? "sonstiges";
}

function appendPositions(
  positions: PositionForRoom[],
  entry: CatalogEntryForAggregator,
  ca: CodeAssignmentForAggregator,
  scope: "polygon" | "wall",
  wallId: string | undefined,
  polygonCtx: FormelContext["polygon"],
  oeffnungenM2: number,
  verschnittPct: number,
  wand?: { flaecheM2: number; laengeM: number; dickeMm: number }
): void {
  if (!entry.lvMapping) return;
  for (const pos of entry.lvMapping.positions) {
    const ctx: FormelContext = {
      polygon: polygonCtx,
      oeffnungenM2,
      verschnittPct,
      wand,
    };
    let result: EvaluationResult;
    try {
      result = evaluateMengenFormel(pos.mengenFormel, ctx);
    } catch {
      continue; // Formel-Fehler — überspringen (bei Backend wird's geloggt)
    }
    positions.push({
      code: ca.code,
      gewerk: entry.gewerk,
      source: ca.source,
      scope,
      wallId,
      hauptgruppe: pos.hauptgruppe,
      untergruppe: pos.untergruppe,
      ozPattern: pos.ozPattern,
      bezeichnung: pos.bezeichnung,
      langtextTemplate: pos.langtextTemplate,
      menge: result.menge,
      einheit: pos.mengenEinheit,
      mengenAusdruck: result.ausdruck,
      mengenFormel: pos.mengenFormel,
    });
  }
}
